/**
 * Email Test Tab — two-column composer (form + live preview) with attachments.
 *
 * Two modes:
 *   • Free-form  → compose with the rich-text editor (EmailBodyEditor).
 *   • Template   → a designed HTML template is shown rendered (true style) and the user only
 *                  fills its {{variables}}. The template HTML is NEVER loaded into the rich-text
 *                  editor (which would strip its styling); the final, variable-substituted HTML
 *                  is sent as-is.
 */

import { useState, useEffect, useMemo } from 'react';
import { useForm } from '@mantine/form';
import { FileButton } from '@mantine/core';
import {
  IconSend,
  IconClock,
  IconAlertCircle,
  IconInfoCircle,
  IconPaperclip,
  IconX,
} from '@tabler/icons-react';
import {
  Stack,
  Group,
  Button,
  Alert,
  Select,
  TextInput,
  Switch,
  Text,
  Badge,
  Grid,
  Divider,
  ActionIcon,
} from '../../../../../components/ui';
import { Card } from '../../../../../components/ui/data-display';
import { EmailBodyEditor } from '../../_shared/EmailBodyEditor';
import { HtmlEmailPreview } from '../../_shared/HtmlEmailPreview';
import { useFileUpload } from '../../../../../components/ccms-components/file-management/hooks/useFileUpload';
import { toast } from '../../../../../components/ui/providers/AlertProvider';
import type { EmailTestTabProps, EmailTestData, EmailAttachmentRef } from '../types';
import { PRIORITIES } from '../../types';
import { decodeMessageBody } from '../../_shared/decodeBody';

function formatBytes(bytes?: number): string {
  if (!bytes || bytes <= 0) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Unique {{variable}} names found in the text. */
function extractVars(text: string): string[] {
  const set = new Set<string>();
  const re = /\{\{\s*([\w.]+)\s*\}\}/g;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text || '')) !== null) set.add(m[1]);
  return Array.from(set);
}

/** Replace {{var}} with its value; leaves {{var}} intact when no value yet (visible in preview). */
function substituteVars(text: string, values: Record<string, string>): string {
  return (text || '').replace(/\{\{\s*([\w.]+)\s*\}\}/g, (_full, k) =>
    values[k] != null && values[k] !== '' ? values[k] : `{{${k}}}`,
  );
}

export function EmailTestTab({ templates, onSend }: EmailTestTabProps) {
  const [sending, setSending] = useState(false);
  const [attachments, setAttachments] = useState<EmailAttachmentRef[]>([]);
  const [varValues, setVarValues] = useState<Record<string, string>>({});

  const { upload, uploading } = useFileUpload({
    domain: 'Communications',
    recordId: 'email-attachments',
    maxSize: 25 * 1024 * 1024,
    onComplete: (files) => {
      setAttachments((prev) => [
        ...prev,
        ...files.map((f) => ({ fileId: f.id, fileName: f.fileName, contentType: f.mimeType, size: f.fileSize })),
      ]);
      if (files.length) toast.success(`${files.length} attachment(s) uploaded`);
    },
    onError: (e) => toast.error(e?.message || 'Attachment upload failed'),
  });

  const form = useForm<EmailTestData>({
    initialValues: {
      useTemplate: false,
      templateId: undefined,
      recipient: '',
      ccEmail: '',
      bccEmail: '',
      replyTo: '',
      fromName: '',
      subject: '',
      body: '',
      isHTML: true,
      priority: 5,
      scheduledDateTime: undefined,
      addToQueue: true,
    },
    validate: {
      recipient: (value) => {
        if (!value || value.trim() === '') return 'Recipient email is required';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())) return 'Invalid email address';
        return null;
      },
      // Subject/body only required for free-form (template mode derives them from the template).
      subject: (value, values) => (values.useTemplate ? null : (!value || value.trim() === '' ? 'Subject is required' : null)),
      body: (value, values) => (values.useTemplate ? null : (!value || value.trim() === '' ? 'Email body is required' : null)),
    },
  });

  const selectedTemplate = form.values.useTemplate && form.values.templateId
    ? templates.find((t) => t.templateID === form.values.templateId)
    : undefined;
  const templateMode = !!selectedTemplate;

  const decodedTemplateBody = useMemo(
    () => (selectedTemplate ? decodeMessageBody(selectedTemplate.body || '') : ''),
    [selectedTemplate],
  );
  const decodedTemplateSubject = useMemo(
    () => (selectedTemplate ? decodeMessageBody(selectedTemplate.subject || '') : ''),
    [selectedTemplate],
  );
  const variables = useMemo(
    () => (templateMode ? extractVars(`${decodedTemplateSubject} ${decodedTemplateBody}`) : []),
    [templateMode, decodedTemplateBody, decodedTemplateSubject],
  );

  // Reset variable values whenever the selected template changes.
  useEffect(() => {
    setVarValues({});
  }, [form.values.templateId, form.values.useTemplate]);

  const finalSubject = templateMode ? substituteVars(decodedTemplateSubject, varValues) : form.values.subject;
  const finalBody = templateMode ? substituteVars(decodedTemplateBody, varValues) : form.values.body;
  const isHtmlPreview = templateMode || form.values.isHTML;

  const handleSubmit = async (values: EmailTestData, addToQueue: boolean) => {
    setSending(true);
    try {
      await onSend({
        ...values,
        subject: finalSubject,
        body: finalBody,
        isHTML: templateMode ? true : values.isHTML,
        payload: templateMode && variables.length > 0 ? JSON.stringify(varValues) : undefined,
        attachments,
        addToQueue,
      });
      form.reset();
      setAttachments([]);
      setVarValues({});
    } catch (err) {
      // Error handled by the hook with toast
    } finally {
      setSending(false);
    }
  };

  const templateOptions = templates
    .filter((t) => t.isActive)
    .map((t) => ({ value: String(t.templateID), label: t.templateName || 'Unnamed Template' }));

  const priorityOptions = PRIORITIES.map((p) => ({ value: String(p.value), label: p.label }));

  // "Add to Queue" enabled when recipient is valid and (free-form has body OR a template is chosen).
  const canSubmit = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.values.recipient.trim())
    && (templateMode ? true : !!form.values.subject.trim() && !!form.values.body.trim());

  return (
    <form onSubmit={form.onSubmit((values) => handleSubmit(values, true))}>
      <Grid gutter="xl">
        {/* LEFT — compose form */}
        <Grid.Col span={{ base: 12, lg: 7 }}>
          <Stack gap="md">
            <Alert color="blue" icon={<IconInfoCircle size={16} />} title="Compose Email">
              Send now or queue for later. Pick a template and just fill its fields, or turn the template off to write a custom email.
            </Alert>

            <Card withBorder padding="lg">
              <Stack gap="md">
                <Switch
                  label="Use Email Template"
                  description="Send a designed template — fill in its fields, keep its style"
                  {...form.getInputProps('useTemplate', { type: 'checkbox' })}
                />

                {form.values.useTemplate && (
                  <Select
                    label="Select Template"
                    placeholder="Choose a template"
                    data={templateOptions}
                    value={form.values.templateId ? String(form.values.templateId) : null}
                    onChange={(value) => form.setFieldValue('templateId', value ? parseInt(value, 10) : undefined)}
                    searchable
                    clearable
                  />
                )}

                <TextInput
                  label="Recipient Email"
                  placeholder="recipient@example.com"
                  required
                  leftSection={<IconSend size={16} />}
                  {...form.getInputProps('recipient')}
                />

                <Group grow>
                  <TextInput label="CC (optional)" placeholder="cc@example.com" {...form.getInputProps('ccEmail')} />
                  <TextInput label="BCC (optional)" placeholder="bcc@example.com" {...form.getInputProps('bccEmail')} />
                </Group>

                <Group grow>
                  <TextInput label="Reply-To (optional)" placeholder="replies@example.com" {...form.getInputProps('replyTo')} />
                  <TextInput label="From name (optional)" placeholder="CCMS Notifications" {...form.getInputProps('fromName')} />
                </Group>

                {templateMode ? (
                  /* TEMPLATE MODE — show the subject + variable inputs only; never the raw HTML editor. */
                  <>
                    <TextInput label="Subject (from template)" value={finalSubject} readOnly variant="filled" />
                    {variables.length > 0 ? (
                      <Card withBorder padding="md">
                        <Text size="sm" fw={500} mb="xs">Template fields</Text>
                        <Stack gap="xs">
                          {variables.map((v) => (
                            <TextInput
                              key={v}
                              label={v.replace(/_/g, ' ')}
                              placeholder={`Value for {{${v}}}`}
                              value={varValues[v] || ''}
                              onChange={(e) => setVarValues((prev) => ({ ...prev, [v]: e.currentTarget.value }))}
                            />
                          ))}
                        </Stack>
                      </Card>
                    ) : (
                      <Text size="xs" c="dimmed">This template has no fields — it will be sent exactly as designed.</Text>
                    )}
                    <Text size="xs" c="dimmed">The template's design is preserved — see the live preview on the right. Turn off "Use Email Template" to write a custom email.</Text>
                  </>
                ) : (
                  /* FREE-FORM MODE — rich-text editor. */
                  <>
                    <TextInput label="Subject" placeholder="Email subject" required {...form.getInputProps('subject')} />
                    <Switch label="Send as HTML" description="Off = plain text" {...form.getInputProps('isHTML', { type: 'checkbox' })} />
                    <EmailBodyEditor
                      label="Email Body"
                      value={form.values.body}
                      onChange={(value) => form.setFieldValue('body', value)}
                      minHeight={260}
                    />
                    {form.errors.body && (
                      <Alert color="red" icon={<IconAlertCircle size={16} />}>{form.errors.body}</Alert>
                    )}
                  </>
                )}

                {/* Attachments */}
                <div>
                  <Group justify="space-between" mb="xs">
                    <Text size="sm" fw={500}>Attachments</Text>
                    <FileButton multiple onChange={(files) => files && files.length > 0 && upload(files)}>
                      {(props) => (
                        <Button {...props} variant="light" size="xs" leftSection={<IconPaperclip size={14} />} loading={uploading}>
                          Add files
                        </Button>
                      )}
                    </FileButton>
                  </Group>
                  {attachments.length === 0 ? (
                    <Text size="xs" c="dimmed">No attachments. Max 25MB per file.</Text>
                  ) : (
                    <Stack gap={4}>
                      {attachments.map((a, i) => (
                        <Group key={a.fileId} justify="space-between" wrap="nowrap"
                          style={{ border: '1px solid var(--mantine-color-default-border)', borderRadius: 6, padding: '4px 8px' }}>
                          <Group gap="xs" wrap="nowrap" style={{ minWidth: 0 }}>
                            <IconPaperclip size={14} />
                            <Text size="xs" truncate>{a.fileName}</Text>
                            {a.size ? <Badge size="xs" variant="light" color="gray">{formatBytes(a.size)}</Badge> : null}
                          </Group>
                          <ActionIcon size="sm" variant="subtle" color="red"
                            onClick={() => setAttachments((prev) => prev.filter((_, idx) => idx !== i))} title="Remove">
                            <IconX size={14} />
                          </ActionIcon>
                        </Group>
                      ))}
                    </Stack>
                  )}
                </div>

                <Select
                  label="Priority"
                  description="Higher priority emails are sent first (1=lowest, 10=highest)"
                  data={priorityOptions}
                  value={String(form.values.priority)}
                  onChange={(value) => form.setFieldValue('priority', parseInt(value || '5', 10))}
                />

                <TextInput
                  label="Schedule Send (Optional)"
                  placeholder="Leave empty to send immediately"
                  type="datetime-local"
                  leftSection={<IconClock size={16} />}
                  value={form.values.scheduledDateTime ? new Date(form.values.scheduledDateTime).toISOString().slice(0, 16) : ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    form.setFieldValue('scheduledDateTime', value ? new Date(value) : undefined);
                  }}
                />

                <Divider />

                <Group justify="flex-end" gap="md">
                  <Button variant="subtle" onClick={() => { form.reset(); setAttachments([]); setVarValues({}); }} disabled={sending}>
                    Clear Form
                  </Button>
                  <Button variant="outline" onClick={() => handleSubmit(form.values, true)} loading={sending} leftSection={<IconClock size={16} />} disabled={!canSubmit}>
                    Add to Queue
                  </Button>
                  <Button type="submit" gradientVariant="primary" loading={sending} leftSection={<IconSend size={16} />} disabled={!canSubmit}>
                    Send Now
                  </Button>
                </Group>
              </Stack>
            </Card>
          </Stack>
        </Grid.Col>

        {/* RIGHT — live preview */}
        <Grid.Col span={{ base: 12, lg: 5 }}>
          <Stack gap="xs">
            <Text size="sm" fw={500}>Preview</Text>
            <div style={{ position: 'sticky', top: 16 }}>
              <Card withBorder padding="md">
                <Text size="xs" c="dimmed">To: {form.values.recipient || '—'}</Text>
                <Text size="sm" fw={600} mb={6}>{finalSubject || '(no subject)'}</Text>
                <Divider mb="sm" />
                {isHtmlPreview ? (
                  <HtmlEmailPreview html={finalBody} height={460} />
                ) : (
                  <Text size="sm" style={{ whiteSpace: 'pre-wrap' }}>{finalBody || 'Your email preview will appear here…'}</Text>
                )}
                {attachments.length > 0 && (
                  <>
                    <Divider my="sm" />
                    <Text size="xs" c="dimmed" mb={4}>{attachments.length} attachment(s)</Text>
                    <Group gap={4}>
                      {attachments.map((a) => (
                        <Badge key={a.fileId} size="sm" variant="light" leftSection={<IconPaperclip size={12} />}>{a.fileName}</Badge>
                      ))}
                    </Group>
                  </>
                )}
              </Card>
            </div>
          </Stack>
        </Grid.Col>
      </Grid>
    </form>
  );
}
